import { useCallback, useEffect, useRef, useState } from 'react';
import { store } from './storage.js';

/** 1 Hz clock for countdowns. One interval per subscriber is fine at this scale. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Foreground-only polling (docs/04 §8): battery is a retention metric. The
 * callback runs immediately on visibility gain and every `everyMs` while
 * visible; never while hidden. A change of `key` (e.g. the search center)
 * restarts the cycle, so the first fetch for the new inputs is immediate.
 */
export function useForegroundPoll(
  cb: () => void, everyMs: number, enabled: boolean, key: string | null = null,
): void {
  const cbRef = useRef(cb);
  cbRef.current = cb;

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      cbRef.current();
      timer = setInterval(() => cbRef.current(), everyMs);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { document.visibilityState === 'visible' ? start() : stop(); };

    onVis();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, [everyMs, enabled, key]);
}

export type LocState = 'unknown' | 'granted' | 'denied';

/**
 * The driver's position, owned by the shell so every tab shares one fix.
 * Re-acquired on every foreground gain and on an interval while visible —
 * a shift is hours long and the first fix must not be the only one.
 *
 * Privacy: full precision lives only in memory for the current request;
 * the stored "last origin" is coarse (storage.ts).
 */
export function useGeolocation(refreshMs = 120_000): {
  origin: { lat: number; lng: number } | null;
  state: LocState;
  fixedAt: number | null;
  refresh: () => void;
} {
  const [origin, setOrigin] = useState<{ lat: number; lng: number } | null>(null);
  const [state, setState] = useState<LocState>('unknown');
  const [fixedAt, setFixedAt] = useState<number | null>(null);

  const refresh = useCallback(() => {
    if (!navigator.geolocation) { setState('denied'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const o = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setOrigin(o); store.origin.set(o); setState('granted'); setFixedAt(Date.now());
      },
      () => setState((s) => (s === 'granted' ? s : 'denied')),
      { maximumAge: 60_000, timeout: 8_000 },
    );
  }, []);

  useForegroundPoll(refresh, refreshMs, true);

  return { origin, state, fixedAt, refresh };
}

/** Once-a-minute clock for countdowns: digits change on the minute, never
 *  per second (docs/11 §5.12). Aligned to the wall clock so all cards tick together. */
export function useMinuteTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;
    const arm = () => {
      const ms = 60_000 - (Date.now() % 60_000);
      id = setTimeout(() => { setNow(Date.now()); arm(); }, ms);
    };
    arm();
    const onVis = () => { if (document.visibilityState === 'visible') setNow(Date.now()); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearTimeout(id); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  return now;
}

/** Trailing-edge debounce for type-ahead. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return v;
}
